<template>
  <div :class="$style.container">
    <dropdown
      align="right"
      :arrow="!$mq.phone && typeof profiles != 'undefined' && profiles.toArray().length > 0"
      :hoverable="!$mq.phone"
      :button-class="$style.dropdownButton"
    >
      <template slot="label">
        <div
          v-if="$mq.phone"
          :class="$style.dropdownButtonAvatar"
        >
          <avatar :name="currentProfile.name ? currentProfile.name : userEmail" :picture="currentProfile.largePicture" :small="$mq.phone" />
        </div>
        <span
          v-else
          :class="[$style.dropdownButtonLabel, 'profile_name']"
        >
          <span>{{ currentProfile && currentProfile.name ? currentProfile.name : userEmail }}</span>
        </span>
      </template>
      <template
        v-if="typeof profiles != 'undefined' && profiles.toArray().length > 0"
        slot="panel"
      >
        <div :class="[$style.dropdownPanel, 'profile_dropdown_panel']">
          <div :class="$style.dropdownPanelGroup">
            <div :class="$style.profileItems">
              <a
                v-for="profile in profiles.toArray()"
                :key="profile.id"
                href="#"
                :class="[$style.profileItem, {[$style.profileItemStateActive]: profile.current}]"
                @click="profileItemClickHandler($event, profile)"
              >
                <table :class="$style.layoutTable">
                  <tr>
                    <td style="vertical-align: top">
                      <div :class="[$style.profileItemAvatar, {[$style.profileItemAvatarStyleShadow]: profile.largePicture}]">
                        <avatar :name="profile.name ? profile.name : userEmail" :picture="profile.largePicture" />
                      </div>
                    </td>
                    <td>
                      <div :class="[$style.profileItemName, 'profile_name']">{{ profile.name ? profile.name : userEmail }}</div>
                      <div
                        v-translate
                        :class="$style.profileItemType"
                      >{{ profile.typeLabel }}</div>
                    </td>
                  </tr>
                </table>
              </a>
            </div>
          </div>
        </div>
      </template>
    </dropdown>
  </div>
</template>

<script>
  import { EVENT_ERROR, EVENT_PROFILE_ITEM_CLICK } from '@events/Toolbar'

  import ProfileCollection from '@models/Profile/collection'
  import LocaleCollection from '@models/Locale/collection'

  import AvatarComponent from '@components/Avatar'
  import DropdownComponent from '@components/Dropdown'

  export default {
    components: {
      avatar: AvatarComponent,
      dropdown: DropdownComponent
    },
    props: {
      userId: {
        type: String
      },
      logoutUrl: {
        type: String
      },
      settingsUrl: {
        type: String
      },
      profiles: {
        type: ProfileCollection,
        required: true
      },
      locales: {
        type: LocaleCollection
      },
      userEmail: {
        type: String
      }
    },
    locales: {
      'en': {
        'private': 'Private',
        'legal': 'Business'
      },
      'ru': {
        'private': 'Приватный',
        'legal': 'Бизнес'
      }
    },
    computed: {
      currentProfile () {
        return this.profiles ? this.profiles.findCurrentProfile() : null
      }
    },
    methods: {
      profileItemClickHandler (e, profile) {
        if (!profile.current) {
          this.changeProfile(profile)
        } else {
          e.stopPropagation()
        }
      },
      changeProfile (profile) {
        this.profiles.switchProfile(this.userId, profile)
          .then(() => {
            this.$sendSystemEvent(EVENT_PROFILE_ITEM_CLICK)
          })
          .catch(e => {
            this.$sendSystemEvent(EVENT_ERROR, e)
          })
      }
    }
  }
</script>

<style lang="scss" module>
  @import "~@stylesheets/includes/vars";
  @import "~@stylesheets/includes/mixins";
  @import "~@stylesheets/common/buttons/button-a";
  @import "~@stylesheets/common/layout-table";

  .layoutTable {
    @include layout-table;
  }

  /**
   * Container
   */
  .container {
    display: flex;
  }

  /**
   * Dropdown button avatar
   * (phone only)
   */
  .dropdownButtonAvatar {
    overflow: hidden;
    width: 28px;
    height: 28px;
    border-radius: 5px;
  }

  /**
   * Dropdown button label
   */
  .dropdownButtonLabel {
    color: #2d3e50;
    font-size: 15px;
  }

  /**
   * Dropdown panel
   */
  .dropdownPanel {
    width: 320px;
  }

  /**
   * Dropdown panel group
   */
  .dropdownPanelGroup {
    &:not(:first-child) {
      border-top: 1px solid #ebeef2;
    }
  }

  /**
   * Profile item
   */
  .profileItem {
    display: block;
    overflow: hidden;
    position: relative;
    padding: 19px 20px 19px 30px;
    text-decoration: none;
    transition: background-color .3s;

    &:not(:first-child) {
      border-top: 1px solid #ebeef2;
    }
  }

  /**
   * Profile item hover state
   */
  .profileItem:not(.profileItemStateActive):hover {
    background-color: #fdf8f9;

    .profileItemDetails,
    .profileItemAvatar {
      opacity: 1;
    }
  }

  /**
   * Profile item active state
   */
  .profileItemStateActive {
    background-color: #fbfbfc;
    cursor: default;

    // @todo
    &:before {
      display: block;
      position: absolute;
      top: 30px;
      right: 20px;
      width: 17px;
      height: 17px;
      content: image-url('tick.svg');
    }

    .profileItemDetails,
    .profileItemAvatar {
      opacity: 1;
    }
  }

  /**
   * Profile item avatar
   */
  .profileItemAvatar {
    overflow: hidden;
    width: 41px;
    height: 41px;
    margin-right: 16px;
    opacity: .7;
    border-radius: 10px;
    transition: opacity .3s;
  }

  .profileItemAvatarStyleShadow {
    box-shadow: 0px 1px 6px 0 rgba(77, 68, 57, 0.6);
  }

  /**
   * Profile item details
   */
  .profileItemDetails {
    display: block;
    opacity: .7;
    transition: opacity .3s;
    width: 180px;
  }

  /**
   * Profile item name
   */
  .profileItemName {
    max-width: 170px;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #2d3e50;
    font-family: $font-special;
    font-size: 16px;
    font-weight: 900;
  }

  /**
   * Profile item type
   */
  .profileItemType {
    color: #2d3e50;
    font-size: 13px;
  }

  /**
   * Add profile button
   */
  .addProfileButton {
    @include button-a1('plus.svg');
  }
</style>



// WEBPACK FOOTER //
// src/components/ProfileDropdown.vue